<template>
  <div
    class="spnod-admin__form--container container mx-0"
    :class="isMobile ? 'px-0' : 'px-3'">
    <b-form @submit.stop.prevent="handleSubmit">
      <b-form-row class="mb-2">
        <b-col>
          <b-form-input
            name="firstName"
            v-model="$v.form.firstName.$model"
            :state="validateState('firstName')"
            type="text"
            placeholder="First Name"
            size="sm"
          ></b-form-input>
        </b-col>
        <b-col>
          <b-form-input
            name="lastName"
            v-model="$v.form.lastName.$model"
            :state="validateState('lastName')"
            type="text"
            placeholder="Last Name"
            size="sm"
          ></b-form-input>
        </b-col>
      </b-form-row>
      <b-form-group
        label-cols="3"
        label-cols-md="2"
        label="DOB"
        label-for="dob"
        class="mb-0"
      >
        <b-form-input
          id="dob"
          name="DOB"
          v-model="$v.form.DOB.$model"
          :state="validateState('DOB')"
          type="date"
          size="sm"
          class="mb-2"
          cols="8"
        ></b-form-input>
      </b-form-group>
      <b-form-input
        name="email"
        v-model="$v.form.email.$model"
        :state="validateState('email')"
        type="email"
        placeholder="Email"
        size="sm"
        class="mb-2"
      ></b-form-input>
      <b-form-group class="mb-2">
        <b-form-input
          name="phone"
          v-model="$v.form.phone.$model"
          :state="validateState('phone')"
          type="tel"
          placeholder="Phone Number"
          size="sm"
          class="mb-0"
        ></b-form-input>
        <b-form-invalid-feedback>
          Must follow this format: 8885551234
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group class="mb-2">
        <p class="spnod-text--small">By providing your phone number, you will receive a text verification code to complete your sign-up. You are able to opt-out of future notifications directly from the text.</p>
      </b-form-group>
      <b-input-group class="mb-2">
        <b-form-input
          name="password"
          v-model="$v.form.password.$model"
          :state="validateState('password')"
          :type="isPasswordDisplayed"
          placeholder="Password"
          size="sm"
          class="mb-0"
        >
        </b-form-input>
        <b-input-group-append>
          <span class="input-group-text small spnod-cursor" @click="handlePassToggle('password')">
            <i v-if="!showPassword" class="fa fa-eye fa-sm"></i>
            <i v-else class="fa fa-eye-slash fa-sm"></i>
          </span>
        </b-input-group-append>
        <b-form-invalid-feedback>
          Password must be at least 8 characters long and contain at least 1 capital letter, 1 number and 1 symbol
        </b-form-invalid-feedback>
      </b-input-group>
      <b-input-group class="mb-4">
        <b-form-input
          name="password2"
          v-model="$v.form.password2.$model"
          :state="validateState('password2')"
          :type="isConfirmDisplayed"
          placeholder="Confirm Password"
          size="sm"
          class="mb-0"
        ></b-form-input>
        <b-input-group-append>
          <span class="input-group-text small spnod-cursor" @click="handlePassToggle('confirm')">
            <i v-if="!showConfirm" class="fa fa-eye fa-sm"></i>
            <i v-else class="fa fa-eye-slash fa-sm"></i>
          </span>
        </b-input-group-append>
        <b-form-invalid-feedback>
          Must match password
        </b-form-invalid-feedback>
      </b-input-group>
      <ul class="spnod-admin__form--list">
        <li>
          <router-link class="spnod-admin__form--link" to="/login">Login</router-link>
        </li>
      </ul>
      <b-form-group class="d-flex justify-content-end">
        <b-button class="spnod-admin__form--button" type="submit" size="sm" :disabled="isDisabled">
          <p v-if="!isSending">Send</p>
          <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </b-button>
      </b-form-group>
    </b-form>
  </div>
</template>

<script>
import { required, email, numeric, alpha, sameAs } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'UserForm',
  mixin: [validationMixin],
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        DOB: '',
        email: '',
        phone: '',
        password: '',
        password2: ''
      },
      showPassword: false,
      showConfirm: false,
      isSending: false
    }
  },
  computed: {
    ...mapGetters(['signedUp']),
    isDisabled() {
      const anyBlanks = Object.entries(this.form).some(arr => arr[0] !== 'dob' && arr[1] === '')
      return anyBlanks || this.$v.form.$anyError
    },
    isPasswordDisplayed() {
      return this.showPassword ? 'text' : 'password'
    },
    isConfirmDisplayed() {
      return this.showConfirm ? 'text' : 'password'
    },
    isMobile() { return window.innerWidth <= 768 }
  },
  methods: {
    ...mapActions(['signUpPortalUser']),
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    handleSubmit: async function (e) {
      this.$v.form.$touch()

      if (this.$v.form.$anyError) {
        return
      }

      this.isSending = true
      const res = await this.signUpPortalUser(this.form)
      this.isSending = false
      if (res) this.$router.push('/verify')
    },
    handlePassToggle(type) {
      if (type === 'password') {
        this.showPassword = !this.showPassword
      } else {
        this.showConfirm = !this.showConfirm
      }
    }
  },
  validations: {
    form: {
      firstName: { required, alpha },
      lastName: { required, alpha },
      DOB: {},
      email: { required, email },
      phone: { required, numeric, isTenDigits: (number) => number.length === 10 },
      password: { required, regex: (pass) => /^(?=.*[A-Z])(?=.*[^aA-zZ0-9])(?=.*[0-9]).{6,}$/.test(pass) },
      password2: { required, samePassword: sameAs('password') }
    }
  }
}
</script>

<style lang="scss">
 .spnod-text--small {
    font-size: 14px;
 }
</style>



// WEBPACK FOOTER //
// src/app/components/Forms/RegistrationForm.vue?5cec2740